import { supabase } from "@/lib/supabase";
import type {
  DbLeadSourceInsert,
  DbLeadSourceUpdate,
  DbLeadSourceRow,
  LeadSource,
} from "@/types/database";
import { mapDbLeadSourceToUiFromView } from "@/mappers";

/**
 * UI analytics row returned by public.v_lead_sources_ui
 * Keep this local unless you want to add it to your shared DB types.
 */
export type DbLeadSourceUiViewRow = {
  id: string;
  name: string;
  sourceType: string;

  active: boolean;
  campaign: string;
  notes: string;

  totalLeads: number;
  contacted: number;
  appointmentsSet: number;
  appointmentsCompleted: number;

  accepted_leads: number;
  rejected_leads: number;
  received_leads: number;
  pending_review_leads: number;

  total_jobs: number;

  connectionRate: number;
  bookingRate: number;
  showRate: number;
  saleRate: number;
  acceptanceRate: number;
  rejectionRate: number;

  callable_leads: number;
  sms_leads: number;
  emailable_leads: number;
  bookable_leads: number;
  manually_overridden_leads: number;
  avg_risk_score: number | null;

  validation_count: number;
  validation_pass_count: number;
  validation_fail_count: number;
  validation_warning_count: number;
  validation_error_count: number;
  avg_validation_score: number | null;

  rejection_reason_counts: Record<string, number>;

  first_lead_received_at: string | null;
  last_lead_received_at: string | null;
  last_accepted_at: string | null;
  last_rejected_at: string | null;
  last_reviewed_at: string | null;
  last_validated_at: string | null;
  last_action_validation_at: string | null;
  last_event_at: string | null;

  tenant_id: string | null;
  created_at: string | null;
  updated_at: string | null;
};

function numeric(value: unknown): number {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function mergeReasonCounts(
  current: Record<string, number> | null | undefined,
  incoming: Record<string, number> | null | undefined,
) {
  const next: Record<string, number> = { ...(current ?? {}) };
  Object.entries(incoming ?? {}).forEach(([key, value]) => {
    next[key] = numeric(next[key]) + numeric(value);
  });
  return next;
}

function recomputeRate(numerator: number, denominator: number) {
  return denominator > 0 ? Math.round((numerator / denominator) * 100) : 0;
}

function mergeLeadSourceUiRows(rows: DbLeadSourceUiViewRow[]) {
  const merged = new Map<string, DbLeadSourceUiViewRow>();

  rows.forEach((row) => {
    const existing = merged.get(row.id);
    if (!existing) {
      merged.set(row.id, {
        ...row,
        rejection_reason_counts: { ...(row.rejection_reason_counts ?? {}) },
      });
      return;
    }

    const acceptedLeads = numeric(existing.accepted_leads) + numeric(row.accepted_leads);
    const receivedLeads = numeric(existing.received_leads) + numeric(row.received_leads);
    const contacted = numeric(existing.contacted) + numeric(row.contacted);
    const appointmentsSet = numeric(existing.appointmentsSet) + numeric(row.appointmentsSet);
    const appointmentsCompleted = numeric(existing.appointmentsCompleted) + numeric(row.appointmentsCompleted);
    const totalJobs = numeric(existing.total_jobs) + numeric(row.total_jobs);
    const validationCount = numeric(existing.validation_count) + numeric(row.validation_count);

    const weightedRiskTotal =
      numeric(existing.avg_risk_score) * numeric(existing.received_leads) +
      numeric(row.avg_risk_score) * numeric(row.received_leads);
    const weightedValidationTotal =
      numeric(existing.avg_validation_score) * numeric(existing.validation_count) +
      numeric(row.avg_validation_score) * numeric(row.validation_count);

    merged.set(row.id, {
      ...existing,
      active: existing.active || row.active,
      totalLeads: numeric(existing.totalLeads) + numeric(row.totalLeads),
      contacted,
      appointmentsSet,
      appointmentsCompleted,
      accepted_leads: acceptedLeads,
      rejected_leads: numeric(existing.rejected_leads) + numeric(row.rejected_leads),
      received_leads: receivedLeads,
      pending_review_leads: numeric(existing.pending_review_leads) + numeric(row.pending_review_leads),
      total_jobs: totalJobs,
      connectionRate: recomputeRate(contacted, acceptedLeads),
      bookingRate: recomputeRate(appointmentsSet, contacted),
      showRate: recomputeRate(appointmentsCompleted, appointmentsSet),
      saleRate: recomputeRate(totalJobs, appointmentsCompleted),
      acceptanceRate: recomputeRate(acceptedLeads, receivedLeads),
      rejectionRate: recomputeRate(
        numeric(existing.rejected_leads) + numeric(row.rejected_leads),
        receivedLeads,
      ),
      callable_leads: numeric(existing.callable_leads) + numeric(row.callable_leads),
      sms_leads: numeric(existing.sms_leads) + numeric(row.sms_leads),
      emailable_leads: numeric(existing.emailable_leads) + numeric(row.emailable_leads),
      bookable_leads: numeric(existing.bookable_leads) + numeric(row.bookable_leads),
      manually_overridden_leads: numeric(existing.manually_overridden_leads) + numeric(row.manually_overridden_leads),
      avg_risk_score: receivedLeads > 0 ? weightedRiskTotal / receivedLeads : null,
      validation_count: validationCount,
      validation_pass_count: numeric(existing.validation_pass_count) + numeric(row.validation_pass_count),
      validation_fail_count: numeric(existing.validation_fail_count) + numeric(row.validation_fail_count),
      validation_warning_count: numeric(existing.validation_warning_count) + numeric(row.validation_warning_count),
      validation_error_count: numeric(existing.validation_error_count) + numeric(row.validation_error_count),
      avg_validation_score: validationCount > 0 ? weightedValidationTotal / validationCount : null,
      rejection_reason_counts: mergeReasonCounts(existing.rejection_reason_counts, row.rejection_reason_counts),
      first_lead_received_at: [existing.first_lead_received_at, row.first_lead_received_at].filter(Boolean).sort()[0] ?? null,
      last_lead_received_at: [existing.last_lead_received_at, row.last_lead_received_at].filter(Boolean).sort().at(-1) ?? null,
      last_accepted_at: [existing.last_accepted_at, row.last_accepted_at].filter(Boolean).sort().at(-1) ?? null,
      last_rejected_at: [existing.last_rejected_at, row.last_rejected_at].filter(Boolean).sort().at(-1) ?? null,
      last_reviewed_at: [existing.last_reviewed_at, row.last_reviewed_at].filter(Boolean).sort().at(-1) ?? null,
      last_validated_at: [existing.last_validated_at, row.last_validated_at].filter(Boolean).sort().at(-1) ?? null,
      last_action_validation_at: [existing.last_action_validation_at, row.last_action_validation_at].filter(Boolean).sort().at(-1) ?? null,
      last_event_at: [existing.last_event_at, row.last_event_at].filter(Boolean).sort().at(-1) ?? null,
      updated_at: [existing.updated_at, row.updated_at].filter(Boolean).sort().at(-1) ?? existing.updated_at,
    });
  });

  return [...merged.values()];
}

export const LeadSourceService = {
  /**
   * Raw master-data rows from public.lead_sources
   * Use this for admin CRUD flows, not analytics dashboards.
   */
  async getAllRaw() {
    const { data, error } = await supabase
      .from("lead_sources")
      .select("*")
      .order("created_at", { ascending: false });

    return {
      data: (data as DbLeadSourceRow[] | null) ?? null,
      error,
    };
  },

  /**
   * UI-facing analytics rows from public.v_lead_sources_ui
   * Use this for the Lead Source Management page.
   */
  async getAll() {
    const { data, error } = await supabase
      .from("v_lead_sources_ui")
      .select("*")
      .order("created_at", { ascending: false });

    if (error || !data) {
      return { data: null, error };
    }

    const uiRows = mergeLeadSourceUiRows(data as DbLeadSourceUiViewRow[]).map(
      mapDbLeadSourceToUiFromView,
    );

    return {
      data: uiRows,
      error: null,
    };
  },

  /**
   * Raw source by ID from public.lead_sources
   */
  async getById(id: string) {
    const { data, error } = await supabase
      .from("lead_sources")
      .select("*")
      .eq("id", id)
      .single();

    return {
      data: (data as DbLeadSourceRow | null) ?? null,
      error,
    };
  },

  /**
   * Analytics row by ID from public.v_lead_sources_ui
   */
  async getUiById(id: string) {
    const { data, error } = await supabase
      .from("v_lead_sources_ui")
      .select("*")
      .eq("id", id)
      .single();

    return {
      data: data ? mapDbLeadSourceToUiFromView(data as DbLeadSourceUiViewRow) : null,
      error,
    };
  },

  /**
   * Lightweight search for analytics screen.
   * Searches only currently valid fields.
   */
  async search(query: string) {
    const trimmed = query.trim();

    let builder = supabase
      .from("v_lead_sources_ui")
      .select("*")
      .order("created_at", { ascending: false });

    if (trimmed) {
      builder = builder.or(
        `name.ilike.%${trimmed}%,sourceType.ilike.%${trimmed}%`,
      );
    }

    const { data, error } = await builder;

    if (error || !data) {
      return { data: null, error };
    }

    return {
      data: (data as DbLeadSourceUiViewRow[]).map(mapDbLeadSourceToUiFromView),
      error: null,
    };
  },

  /**
   * Create raw source master row.
   */
  async create(payload: DbLeadSourceInsert) {
    const { data, error } = await supabase
      .from("lead_sources")
      .insert([payload])
      .select("*")
      .single();

    return {
      data: (data as DbLeadSourceRow | null) ?? null,
      error,
    };
  },

  /**
   * Update raw source master row.
   */
  async update(id: string, payload: DbLeadSourceUpdate) {
    const { data, error } = await supabase
      .from("lead_sources")
      .update(payload)
      .eq("id", id)
      .select("*")
      .single();

    return {
      data: (data as DbLeadSourceRow | null) ?? null,
      error,
    };
  },

  /**
   * Delete raw source master row.
   */
  async delete(id: string) {
    const { error } = await supabase
      .from("lead_sources")
      .delete()
      .eq("id", id);

    return { error };
  },

  /**
   * Activate/Deactivate a lead source
   */
  async setActive(id: string, isActive: boolean) {
    const { data, error } = await supabase
      .from("lead_sources")
      .update({ is_active: isActive })
      .eq("id", id)
      .select("*")
      .single();
  
    return { data, error };
  }
};
